import { useMemo, useState } from "react";
import {
  Stack,
  Group,
  Title,
  Text,
  ThemeIcon,
  Paper,
  TextInput,
  Button,
  Tabs,
  Badge,
  Modal,
  Alert,
} from "@mantine/core";
import {
  IconBriefcase,
  IconSearch,
  IconRefresh,
  IconTrash,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { apWorkbenchApi } from "../../../api/apWorkbench.api";
import { apiError } from "../../../api/client";
import { useAuth } from "../../../auth/AuthContext";
import { InvoicesTable } from "./InvoicesTable";
import { InvoiceReviewPage } from "./InvoiceReviewPage";
import { tabLabel } from "./helpers";

const PAGE_SIZE = 25;
const TABS = ["pending_resolution", "ready", "posted", "all"];

/**
 * AP Workbench landing page.
 *
 *   `?invoiceId=...`  → full-width review for that invoice.
 *   else              → list of invoices with filters + tabs.
 *
 * Using a query parameter (rather than a nested route) lets us keep the
 * single-route-per-module convention in App.jsx while still supporting
 * deep-links like `/modules/ap-workbench?invoiceId=<id>` (used by the
 * Tester response link).
 */
export default function ApWorkbenchPage() {
  const [params, setParams] = useSearchParams();
  const invoiceId = params.get("invoiceId");

  if (invoiceId) {
    return (
      <InvoiceReviewPage
        invoiceId={invoiceId}
        onBack={() => {
          const next = new URLSearchParams(params);
          next.delete("invoiceId");
          setParams(next, { replace: false });
        }}
      />
    );
  }

  return (
    <InvoicesListView
      onOpen={(id) => {
        const next = new URLSearchParams(params);
        next.set("invoiceId", id);
        setParams(next);
      }}
    />
  );
}

function InvoicesListView({ onOpen }) {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "pending_resolution");
  const [search, setSearch] = useState(params.get("search") || "");
  const [searchInput, setSearchInput] = useState(search);
  const [supplier, setSupplier] = useState("");
  const [supplierInput, setSupplierInput] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryParams = useMemo(() => {
    const p = { tab, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
    if (search.trim()) p.search = search.trim();
    if (supplier.trim()) p.supplier = supplier.trim();
    return p;
  }, [tab, page, search, supplier]);

  const listQ = useQuery({
    queryKey: ["ap-workbench", "list", queryParams],
    queryFn: () => apWorkbenchApi.listInvoices(queryParams),
    keepPreviousData: true,
  });

  const countsQ = useQuery({
    queryKey: ["ap-workbench", "counts"],
    queryFn: apWorkbenchApi.counts,
    refetchInterval: 30_000,
  });

  const deleteAllMut = useMutation({
    mutationFn: (confirm) => apWorkbenchApi.deletePendingResolution(confirm),
    onSuccess: (data) => {
      notifications.show({
        color: "red",
        title: "Pending invoices deleted",
        message: `Removed ${data.deletedCount} invoice${
          data.deletedCount === 1 ? "" : "s"
        } from pending resolution.`,
      });
      setDeleteModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["ap-workbench"] });
    },
    onError: (e) => {
      notifications.show({ color: "red", message: apiError(e) });
    },
  });

  const updateTab = (v) => {
    setTab(v);
    setPage(1);
    const next = new URLSearchParams(params);
    next.set("tab", v);
    setParams(next, { replace: true });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" wrap="wrap" align="flex-start">
        <Group gap="md">
          <ThemeIcon size={42} radius="md" variant="light" color="violet">
            <IconBriefcase size={24} />
          </ThemeIcon>
          <div>
            <Title order={2}>AP Workbench</Title>
            <Text c="dimmed">
              Review AP invoices received from Duvo, fix mismatched lines, and
              mark them ready for SAP.
            </Text>
          </div>
        </Group>
      </Group>

      <Paper p="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search invoice number…"
            leftSection={<IconSearch size={14} />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(searchInput);
                setPage(1);
              }
            }}
            style={{ flex: 1, minWidth: 220 }}
          />
          <TextInput
            placeholder="Filter by supplier code or name…"
            value={supplierInput}
            onChange={(e) => setSupplierInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSupplier(supplierInput);
                setPage(1);
              }
            }}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Button
            variant="light"
            onClick={() => {
              setSearch(searchInput);
              setSupplier(supplierInput);
              setPage(1);
            }}
          >
            Apply
          </Button>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={14} />}
            onClick={() => {
              listQ.refetch();
              countsQ.refetch();
            }}
            loading={listQ.isFetching}
          >
            Refresh
          </Button>
        </Group>
      </Paper>

      <Group justify="space-between" align="center" wrap="wrap">
        <Tabs value={tab} onChange={updateTab} variant="pills">
          <Tabs.List>
            {TABS.map((t) => (
              <Tabs.Tab
                key={t}
                value={t}
                rightSection={
                  <Badge
                    size="xs"
                    variant={tab === t ? "filled" : "light"}
                    color={tab === t ? "blue" : "gray"}
                  >
                    {countsQ.data?.[t] ?? "…"}
                  </Badge>
                }
              >
                {tabLabel(t)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        {tab === "pending_resolution" && user?.isAdmin && (
          <Button
            color="red"
            variant="light"
            leftSection={<IconTrash size={14} />}
            onClick={() => setDeleteModalOpen(true)}
            disabled={!countsQ.data?.pending_resolution}
          >
            Delete ALL pending (admin)
          </Button>
        )}
      </Group>

      <BulkDeleteModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        count={countsQ.data?.pending_resolution ?? 0}
        loading={deleteAllMut.isPending}
        onConfirm={(text) => deleteAllMut.mutate(text)}
      />

      <InvoicesTable
        rows={listQ.data?.invoices || []}
        total={listQ.data?.total || 0}
        loading={listQ.isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onOpen={onOpen}
      />
    </Stack>
  );
}

function BulkDeleteModal({ opened, onClose, count, loading, onConfirm }) {
  const [text, setText] = useState("");

  // Reset the input each time the modal is re-opened so a previous "DELETE"
  // doesn't carry over.
  const handleClose = () => {
    setText("");
    onClose();
  };

  const canConfirm = text === "DELETE" && count > 0 && !loading;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />
          <Text fw={600}>Delete ALL pending invoices?</Text>
        </Group>
      }
      centered
    >
      <Stack gap="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          This will permanently delete <strong>{count}</strong> invoice
          {count === 1 ? "" : "s"} currently in the <em>Pending resolution</em>{" "}
          tab, along with all their lines. Invoices already pushed to SAP are
          not affected. This action cannot be undone.
        </Alert>
        <Text size="sm">
          Type <strong>DELETE</strong> below to confirm.
        </Text>
        <TextInput
          autoFocus
          placeholder="DELETE"
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canConfirm) onConfirm(text);
          }}
          data-autofocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={14} />}
            disabled={!canConfirm}
            loading={loading}
            onClick={() => onConfirm(text)}
          >
            Delete {count} invoice{count === 1 ? "" : "s"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
